import React, { forwardRef, useCallback, useEffect, useLayoutEffect, useRef } from "react";
import Paper from "@mui/material/Paper";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import OpenInFullRoundedIcon from "@mui/icons-material/OpenInFullRounded";

// A captura do ponteiro mantém o movimento mesmo ao passar sobre o PDF (iframe).
const PapelArrastavel = forwardRef(function PapelArrastavel({ children, sx, rotuloJanela = "janela", ...props }, forwardedRef) {
  const papelRef = useRef(null);
  const arrasteRef = useRef(null);

  // Mantém os mesmos elementos do PDF/editor ao alterar a geometria.
  const posicionar = useCallback((left, top, width, height) => {
    const papel = papelRef.current;
    if (!papel) return;
    const tela = papel.ownerDocument.defaultView || window;
    const largura = Math.min(width, Math.max(1, tela.innerWidth - 16));
    const altura = Math.min(height, Math.max(1, tela.innerHeight - 16));
    Object.assign(papel.style, {
      position: "fixed", margin: "0", transform: "none",
      left: `${Math.max(8, Math.min(left, tela.innerWidth - largura - 8))}px`,
      top: `${Math.max(8, Math.min(top, tela.innerHeight - altura - 8))}px`,
      width: `${largura}px`, height: `${altura}px`,
    });
  }, []);

  const mover = useCallback((dx, dy) => {
    const rect = papelRef.current?.getBoundingClientRect();
    if (rect) posicionar(rect.left + dx, rect.top + dy, rect.width, rect.height);
  }, [posicionar]);

  const redimensionar = useCallback((dx, dy, direcao = "se") => {
    const rect = papelRef.current?.getBoundingClientRect();
    if (!rect) return;
    const tela = papelRef.current.ownerDocument.defaultView || window;
    const minWidth = Math.min(360, tela.innerWidth - 16);
    const minHeight = Math.min(260, tela.innerHeight - 16);
    let { left, top, right, bottom } = rect;
    if (direcao.includes("e")) right = Math.max(left + minWidth, Math.min(right + dx, tela.innerWidth - 8));
    if (direcao.includes("w")) left = Math.max(8, Math.min(left + dx, right - minWidth));
    if (direcao.includes("s")) bottom = Math.max(top + minHeight, Math.min(bottom + dy, tela.innerHeight - 8));
    if (direcao.includes("n")) top = Math.max(8, Math.min(top + dy, bottom - minHeight));
    posicionar(left, top, right - left, bottom - top);
  }, [posicionar]);

  useLayoutEffect(() => {
    const rect = papelRef.current?.getBoundingClientRect();
    if (rect) posicionar(rect.left, rect.top, rect.width, rect.height);
  }, [posicionar]);

  useEffect(() => {
    const reposicionar = () => mover(0, 0);
    const tela = papelRef.current?.ownerDocument.defaultView || window;
    tela.addEventListener("resize", reposicionar);
    return () => tela.removeEventListener("resize", reposicionar);
  }, [mover]);

  return (
    <Paper
      {...props}
      sx={[
        { position: "fixed", left: "50%", top: "50%", transform: "translate(-50%, -50%)", display: "flex", flexDirection: "column", overflow: "hidden", pb: "20px", pointerEvents: "auto", boxSizing: "border-box", maxWidth: "calc(100vw - 16px)", maxHeight: "calc(100dvh - 16px)" },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
      ref={(node) => {
        papelRef.current = node;
        if (typeof forwardedRef === "function") forwardedRef(node);
        else if (forwardedRef) forwardedRef.current = node;
      }}
      onPointerDown={(event) => {
        if (event.button !== 0) return;
        const controle = event.target.closest("[data-redimensionar-modal]");
        const redimensionando = Boolean(controle);
        if (!redimensionando && !event.target.closest("[data-arrastar-modal]")) return;
        event.preventDefault();
        arrasteRef.current = { x: event.clientX, y: event.clientY, redimensionando, direcao: controle?.dataset.redimensionarModal || "se" };
        event.currentTarget.setPointerCapture(event.pointerId);
      }}
      onPointerMove={(event) => {
        const inicio = arrasteRef.current;
        if (!inicio) return;
        if (inicio.redimensionando) redimensionar(event.clientX - inicio.x, event.clientY - inicio.y, inicio.direcao);
        else mover(event.clientX - inicio.x, event.clientY - inicio.y);
        arrasteRef.current = { ...inicio, x: event.clientX, y: event.clientY };
      }}
      onPointerUp={(event) => {
        arrasteRef.current = null;
        if (event.currentTarget.hasPointerCapture(event.pointerId)) {
          event.currentTarget.releasePointerCapture(event.pointerId);
        }
      }}
      onPointerCancel={() => { arrasteRef.current = null; }}
      onLostPointerCapture={() => { arrasteRef.current = null; }}
      onKeyDown={(event) => {
        const controle = event.target.closest("[data-redimensionar-modal]");
        const redimensionando = Boolean(controle);
        if (!redimensionando && !event.target.closest("[data-arrastar-modal]")) return;
        const movimentos = { ArrowLeft: [-20, 0], ArrowRight: [20, 0], ArrowUp: [0, -20], ArrowDown: [0, 20] };
        if (!movimentos[event.key]) return;
        event.preventDefault();
        if (redimensionando) redimensionar(...movimentos[event.key], controle.dataset.redimensionarModal || "se");
        else mover(...movimentos[event.key]);
      }}
    >
      {children}
      {[
        ["n", "borda superior", { top: 0, left: 16, right: 16, height: 8 }],
        ["s", "borda inferior", { bottom: 0, left: 16, right: 28, height: 8 }],
        ["e", "borda direita", { top: 16, bottom: 20, right: 0, width: 8 }],
        ["w", "borda esquerda", { top: 16, bottom: 16, left: 0, width: 8 }],
        ["nw", "quina superior esquerda", { top: 0, left: 0, width: 16, height: 16 }],
        ["ne", "quina superior direita", { top: 0, right: 0, width: 16, height: 16 }],
        ["sw", "quina inferior esquerda", { bottom: 0, left: 0, width: 16, height: 16 }],
      ].map(([direcao, rotulo, geometria]) => (
        <button key={direcao} type="button" data-redimensionar-modal={direcao}
          aria-label={`Redimensionar ${rotuloJanela} pela ${rotulo}`}
          style={{ position: "absolute", ...geometria, zIndex: 3, padding: 0, border: 0, background: "transparent", cursor: `${direcao}-resize`, touchAction: "none" }} />
      ))}
      <Tooltip title="Arraste para aumentar ou reduzir a janela" arrow>
        <IconButton
          type="button"
          data-redimensionar-modal="se"
          aria-label={`Redimensionar ${rotuloJanela}. Arraste ou use as teclas de seta.`}
          size="small"
          sx={{ position: "absolute", right: 0, bottom: 0, width: 28, height: 20, borderRadius: 0, cursor: "nwse-resize", touchAction: "none", color: "var(--sear-azul, #0767d8)" }}
        >
          <OpenInFullRoundedIcon sx={{ fontSize: 16 }} />
        </IconButton>
      </Tooltip>
    </Paper>
  );
});

export default PapelArrastavel;
